import { clsx } from 'clsx'

import { Card, Flex, Text } from '@buffer-mono/popcorn'

import { SectionCard } from '../../../components/SectionCard'

import styles from './BlankMetricsChart.module.css'

import type { JSX } from 'react'

const Y_AXIS_TICKS = [100, 80, 60, 40, 20]
const X_AXIS_TICKS = ['Jan', 'Mar', 'May', 'Jul', 'Sep', 'Nov']

// Dummy curves drawn in a 0–100 × 0–40 viewBox (y inverted: 0 = top/100).
// Decorative only — gentle waves with horizontal control handles so the lines
// read as smooth sample data without steep, near-vertical sections.
const POSTS_CURVE =
  'M0,38 C12.5,38 12.5,10 25,10 C37.5,10 37.5,36 50,36 C62.5,36 62.5,12 75,12 C87.5,12 87.5,34 100,34'
const REACH_CURVE =
  'M0,32 C15,32 15,20 30,20 C42.5,20 42.5,30 55,30 C67.5,30 67.5,8 80,8 C90,8 90,30 100,30'

/**
 * The blank chart card on its own, so a caller can place it in a different
 * section shell — the public preview reuses it for the Followers and Posts
 * placeholders.
 */
export function BlankMetricsChartCard(): JSX.Element {
  return (
    <Card padding="small" className={styles.chartCard}>
      <Flex gap="md" align="center" className={styles.legend} aria-hidden>
        <Flex gap="2xs" align="center">
          <Flex
            aria-hidden
            className={clsx(styles.legendDot, styles.legendDotPosts)}
          />
          <Text size="sm" color="subtle">
            Posts
          </Text>
        </Flex>
        <Flex gap="2xs" align="center">
          <Flex
            aria-hidden
            className={clsx(styles.legendDot, styles.legendDotReach)}
          />
          <Text size="sm" color="subtle">
            Reach
          </Text>
        </Flex>
      </Flex>

      <Flex gap="sm" align="stretch" className={styles.chartBody} aria-hidden>
        <Flex
          direction="column"
          justify="between"
          align="end"
          className={styles.yAxis}
        >
          {Y_AXIS_TICKS.map((tick) => (
            <Text key={tick} size="sm" color="subtle">
              {tick}
            </Text>
          ))}
        </Flex>
        <Flex className={styles.plot}>
          <svg
            className={styles.curves}
            viewBox="0 0 100 40"
            preserveAspectRatio="none"
          >
            <path className={styles.lineSolid} d={POSTS_CURVE} />
            <path className={styles.lineDashed} d={REACH_CURVE} />
          </svg>
        </Flex>
      </Flex>

      <Flex justify="between" className={styles.xAxis} aria-hidden>
        {X_AXIS_TICKS.map((tick) => (
          <Text key={tick} size="sm" color="subtle">
            {tick}
          </Text>
        ))}
      </Flex>

      {/* Washes the chart out so it reads as a preview, not live data. */}
      <Flex aria-hidden className={styles.washOverlay} />
    </Card>
  )
}

/** Empty-state preview of the metrics-over-time line chart. */
export function BlankMetricsChart(): JSX.Element {
  return (
    <SectionCard>
      <SectionCard.Header>
        <SectionCard.Title>Metrics</SectionCard.Title>
      </SectionCard.Header>
      <BlankMetricsChartCard />
    </SectionCard>
  )
}
